/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    DeviceDetailsDto,
    DevicesListDto,
    DevicesListRequestParams,
    RegistrationHistoryItemDto,
    FirmwareHistoryListDto,
    StatusHistoryListDto,
    OperatorHistoryListDto,
} from './devicesClient.typing';
import {
    registrationHistoryItemFactory,
    deviceDetailsFactory,
    deviceDtoFactory,
    firmwareHistoryItemFactory,
    operatorHistoryItemFactory,
    statusHistoryItemFactory,
} from './devicesFactories';

const DEVICES_LIST_URL = /\/1.0.0\/devices$/;
const DEVICE_DETAILS_URL = /\/devices\/\w+$/;
const FIRMWARE_HISTORY = /\/devices\/\w+\/firmwareVersionHistory/;
const STATUS_HISTORY = /\/devices\/\w+\/statusHistory/;
const OPERATOR_HISTORY = /\/devices\/\w+\/operatorHistory/;
const REGISTRATION_HISTORY = /\/devices\/\w+\/registrationHistory/;

class DevicesFeatureMock extends BaseFeatureMock {
    public readonly listDevices = this.route<DevicesListDto, DevicesListRequestParams>({
        method: 'post',
        url: DEVICES_LIST_URL,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);
            const data: DevicesListDto = {
                afterKeyFactoryNumber: '1431_re8932',
                devices: deviceDtoFactory.list({ count: body.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly deviceDetails = this.route<DeviceDetailsDto>({
        method: 'get',
        url: DEVICE_DETAILS_URL,
        defaultResponse: () => {
            const data: DeviceDetailsDto = deviceDetailsFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly firmwareHistory = this.route<FirmwareHistoryListDto>({
        method: 'get',
        url: FIRMWARE_HISTORY,
        defaultResponse: (config) => {
            const data: FirmwareHistoryListDto = {
                count: config.params.limit * 3,
                items: firmwareHistoryItemFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly statusHistory = this.route<StatusHistoryListDto>({
        method: 'get',
        url: STATUS_HISTORY,
        defaultResponse: (config) => {
            const data: StatusHistoryListDto = {
                count: config.params.limit * 3,
                items: statusHistoryItemFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly operatorHistory = this.route<OperatorHistoryListDto>({
        method: 'get',
        url: OPERATOR_HISTORY,
        defaultResponse: (config) => {
            const data: OperatorHistoryListDto = {
                count: config.params.limit * 3,
                items: operatorHistoryItemFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly registrationHistory = this.route<RegistrationHistoryItemDto[]>({
        method: 'get',
        url: REGISTRATION_HISTORY,
        defaultResponse: () => {
            const data: RegistrationHistoryItemDto[] = registrationHistoryItemFactory.list({ count: 5 });

            return [200, data];
        },
        delayMs: 300,
    });

    protected onReset(): void {
        deviceDtoFactory.resetCount();
        deviceDetailsFactory.resetCount();
        firmwareHistoryItemFactory.resetCount();
        statusHistoryItemFactory.resetCount();
        operatorHistoryItemFactory.resetCount();
        registrationHistoryItemFactory.resetCount();
    }
}

export const devicesMock = new DevicesFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addDevicesMockApi(mockAdapter: MockAdapter): void {
    devicesMock.register(mockAdapter, { delay: true });
}
